import type { RadiatorKind } from "./emitters.js";
import type { Ruleset } from "./types.js";

/**
 * Product catalogues: the data that lets the engine NAME an emitter.
 *
 * `assessEmitters` can already say a radiator is 400 W short at 45°C. It cannot say
 * what to put there instead, and MIS 3005-D §3.8.1(c) requires the design pack to
 * state "emitter types and dimensions". This file holds the product data that turns
 * a shortfall into a specification.
 *
 * ---------------------------------------------------------------------------
 * THE FILE SHAPE A SUPPLIED CATALOGUE MUST TAKE
 * ---------------------------------------------------------------------------
 * Send one file per manufacturer range, as CSV or JSON. Every row is one orderable
 * size. Required columns, using exactly these names:
 *
 *   manufacturer      text    "Stelrad"
 *   range             text    "Softline Compact"
 *   model             text    the manufacturer's own designation for the size
 *   partNumber        text    order code / UIN / SKU        (optional but wanted)
 *   panelType         text    K1 | P+ | K2 | K3             (panel radiators only)
 *   kind              text    panel-radiator | column-radiator | towel-rail |
 *                             natural-convector | fan-convector
 *   heightMm          number  overall height
 *   lengthMm          number  overall length
 *   depthMm           number  overall depth  (optional, but a depth constraint
 *                             cannot be applied to rows that omit it)
 *   ratedOutputW      number  heat output in WATTS
 *   ratedDeltaTK      number  the ΔT the output is quoted at — 50 for BS EN 442
 *                             (75/65/20), 60 for an older BS 3528 (90/70/20) sheet.
 *                             MUST be stated. It is not safe to assume.
 *   ratingBasis       text    the flow/return/room triple verbatim, "75/65/20"
 *   exponent          number  the BS EN 442-2 characteristic exponent n. Ask for it
 *                             explicitly and look past the output table: Stelrad
 *                             print n in a SEPARATE "EN 442 certification data"
 *                             table further down the same sheet, and it would have
 *                             been easy to conclude they do not publish it at all.
 *                             LEAVE BLANK only if it is genuinely absent — a blank
 *                             makes the engine warn and fall back to the provisional
 *                             default; a guessed value would silence that warning,
 *                             which is the one thing to avoid.
 *   ratedOutputBtu    number  the manufacturer's own Btu/hr figure, if the sheet
 *                             prints one (optional). Kept only so the watt
 *                             transcription stays checkable — see the test.
 *
 * Also send, once per file: the document title, its issue/revision date, and a URL
 * or a copy of the PDF. Without those the catalogue cannot ever move off
 * "provisional", because there is nothing to check it against.
 *
 * ---------------------------------------------------------------------------
 * PROVENANCE
 * ---------------------------------------------------------------------------
 * Every catalogue seeded here is marked `provisional`, and any selection made from
 * one carries a compliance-failure warning (see `selectEmitters` in @sts/calc).
 * `CatalogueProvenanceNote.toVerify` on each one says exactly what would close ITS
 * gap — the gap is not the same for all of them, and saying so plainly is the point.
 *
 * STELRAD_SOFTLINE_COMPACT (102 rows) has been re-extracted from its published PDF
 * and compared field by field against this file, TWICE, by two people — type,
 * height, length, Stelrad UIN, watts and Btu/hr all match, nothing was omitted, and
 * nothing is here that is not in the sheet. Its stated remaining gap is that the PDF
 * was retrieved from a third-party product-data host rather than from stelrad.com.
 *
 * STELRAD_COMPACT (184 rows), PURMO_COMPACT (90 rows) and MYSON_SELECT_COMPACT (43
 * rows) were each re-extracted from a first-party manufacturer PDF — stelrad.com or
 * purmo.com directly, or (Myson) a manufacturer technical guide hosted by a
 * merchant — checked against a rendered image of the actual source page (not just
 * against the machine-extracted text, which garbled several cells this file's own
 * commit history shows were transcribed correctly anyway) and against the sheet's
 * own Btu/hr column. But each was checked ONLY ONCE, by one person in one session —
 * not independently reproduced by a second person the way Softline Compact was. That
 * is a real, stated gap and not a formality: it is the difference between "I checked
 * my own transcription" and "somebody else got the same figures independently", and
 * only the second one is the standard this file otherwise holds itself to. So all
 * three stay `provisional` alongside Softline Compact, for a narrower reason.
 *
 * No internal consistency check — proportionality, a Btu/hr cross-check, a part
 * number decode — can ever detect a source document that has been superseded, or
 * misread the same way twice by the one person who read it, which is precisely why
 * "our arithmetic agrees with itself" must not be allowed to graduate into
 * "verified" on its own.
 */

/**
 * Steel panel radiator construction, in the UK trade's own notation.
 *
 * The digits count panels and the letters count convector fins, and the two are
 * what set the output: K1 is one panel with one fin set, P+ is one panel with two,
 * K2 is two panels with two, K3 is three with three. A K2 puts out roughly 1.75×
 * a K1 of the same height and length, which is why "same size, more panels" is the
 * first move when a room is short at a heat pump flow temperature.
 */
export type PanelRadiatorType = "K1" | "P+" | "K2" | "K3";

export interface RadiatorCatalogueEntry {
  /** Stable key, unique within the catalogue. Quoted in the design pack. */
  readonly id: string;
  readonly manufacturer: string;
  readonly range: string;
  readonly model: string;
  /** Manufacturer's order code — Stelrad calls it a UIN. */
  readonly partNumber?: string;
  /** Which output characteristic applies. Drives the exponent fallback. */
  readonly kind: RadiatorKind;
  readonly panelType?: PanelRadiatorType;
  readonly heightMm: number;
  readonly lengthMm: number;
  /**
   * Overall depth, mm. A depth constraint EXCLUDES entries that leave this undefined
   * rather than passing them — "might fit" is not "fits".
   *
   * Undefined for every seeded Stelrad entry, and the reason is worth stating
   * exactly, because it is not "the sheet is silent". The sheet carries a dimensioned
   * side-view with three depth figures per panel type — 83/61/51 mm for K1,
   * 101/77/62 for P+, 124/100/73 for K2 — but which is the overall depth, which is
   * the panel body and which is the projection including brackets is carried by the
   * DRAWING, not by any text. Only the smallest of the three is labelled in words,
   * as "Wall to tap centre" in the EN 442 table. Picking one of the other two would
   * be reading a diagram nobody here has seen, so none is recorded.
   */
  readonly depthMm?: number;
  /** Heat output in watts at `ratedDeltaTK`. */
  readonly ratedOutputW: number;
  /** The ΔT the output above is quoted at, K. Never assumed — always from the sheet. */
  readonly ratedDeltaTK: number;
  /** The flow/return/room triple, verbatim from the sheet, e.g. "75/65/20". */
  readonly ratingBasis: string;
  /**
   * The BS EN 442-2 characteristic exponent n, where the manufacturer publishes it.
   *
   * Populated for every seeded entry, from the "EN 442 CERTIFICATION DATA — CETIAT
   * TESTED IN ACCORDANCE WITH BS EN 442" table printed on the same Stelrad sheet as
   * the outputs. It is published per panel type and height, and it is NOT 1.3: the
   * real figures run 1.29 to 1.34, and using the 1.3 default instead moves the
   * output at a 45°C flow temperature by up to about 1.5% — small, but it is the
   * difference between a derived number and a guessed one.
   *
   * Left undefined only where a catalogue genuinely does not publish n. That makes
   * the selection fall back to the provisional per-kind default AND warn about
   * having done so. Filling in 1.3 because 1.3 is usually right would suppress that
   * warning, and the warning is the point.
   */
  readonly exponent?: number;
  /**
   * The manufacturer's own Btu/hr figure, where the sheet prints one.
   *
   * Carried for one reason: it makes the watt column independently checkable
   * forever. `emitter-catalogue.test.ts` reproduces it from the watts and fails on
   * a mistyped digit. It is not used in any calculation.
   */
  readonly ratedOutputBtuPerHour?: number;
}

/**
 * What would have to happen for a catalogue to stop being provisional.
 *
 * `SourceRef` says where a value came from. This says what is still owed — the gap
 * between "somebody typed this in from a PDF" and "an auditor can stand on it".
 */
export interface CatalogueProvenanceNote {
  /** Exact URL or document the entries were transcribed from. */
  readonly retrievedFrom: string;
  /** ISO date of retrieval. */
  readonly retrievedOn: string;
  /** Checks actually performed on the transcription, so they are not re-guessed. */
  readonly checks: readonly string[];
  /** The specific outstanding items. Empty would mean the data can be promoted. */
  readonly toVerify: readonly string[];
}

export interface RadiatorCatalogue {
  readonly entries: readonly RadiatorCatalogueEntry[];
  readonly note: CatalogueProvenanceNote;
}

// ---------------------------------------------------------------------------
// Seed: Stelrad Softline Compact, ΔT50
// ---------------------------------------------------------------------------

/**
 * One printed row: [lengthMm, part number, watts, Btu/hr].
 *
 * The data is kept as tuples rather than as a hundred object literals because the
 * source is a table and this stays a table: a column that has slipped by one is
 * visible here and invisible in a wall of objects. The builder below names the
 * columns exactly once.
 */
type PanelRow = readonly [lengthMm: number, uin: string, watts: number, btu: number];

/**
 * Stelrad "Softline Compact", ΔT50 (75/65/20°C), heights 300/450/600/700.
 *
 * Transcribed from the specification sheet named in the ruleset source below —
 * the whole of the printed table, not a chosen subset, so that what is here can be
 * checked against what is there. Blank cells in the source (P+ is only made in a
 * subset of sizes) are absent here rather than filled in.
 */
const STELRAD_ROWS: ReadonlyArray<
  readonly [heightMm: number, type: PanelRadiatorType, rows: readonly PanelRow[]]
> = [
  [
    300,
    "K1",
    [
      [500, "80301105", 255, 870],
      [1000, "80301110", 509, 1737],
      [1500, "80301115", 764, 2608],
      [2000, "80301120", 1018, 3474],
    ],
  ],
  [
    300,
    "K2",
    [
      [500, "80302205", 491, 1676],
      [1000, "80302210", 982, 3352],
      [1500, "80302215", 1473, 5027],
      [2000, "80302220", 1964, 6703],
    ],
  ],
  [
    450,
    "K1",
    [
      [400, "80451104", 302, 1031],
      [500, "80451105", 378, 1290],
      [600, "80451106", 454, 1550],
      [700, "80451107", 529, 1805],
      [800, "80451108", 605, 2065],
      [900, "80451109", 680, 2321],
      [1000, "80451110", 756, 2580],
      [1100, "80451111", 832, 2840],
      [1200, "80451112", 907, 3096],
      [1400, "80451114", 1058, 3611],
      [1600, "80451116", 1210, 4130],
      [1800, "80451118", 1361, 4645],
      [2000, "80451120", 1512, 5160],
    ],
  ],
  [
    450,
    "P+",
    [
      [1000, "80452110", 1055, 3601],
      [1100, "80452111", 1161, 3962],
      [1200, "80452112", 1266, 4321],
      [1400, "80452114", 1477, 5041],
      [1600, "80452116", 1688, 5761],
    ],
  ],
  [
    450,
    "K2",
    [
      [400, "80452204", 548, 1870],
      [500, "80452205", 686, 2341],
      [600, "80452206", 823, 2809],
      [700, "80452207", 960, 3276],
      [800, "80452208", 1097, 3744],
      [900, "80452209", 1234, 4212],
      [1000, "80452210", 1371, 4679],
      [1100, "80452211", 1508, 5147],
      [1200, "80452212", 1645, 5614],
      [1400, "80452214", 1919, 6550],
      [1600, "80452216", 2194, 7488],
      [1800, "80452218", 2468, 8423],
      [2000, "80452220", 2742, 9358],
    ],
  ],
  [
    600,
    "K1",
    [
      [400, "80601104", 392, 1338],
      [500, "80601105", 490, 1672],
      [600, "80601106", 588, 2007],
      [700, "80601107", 686, 2341],
      [800, "80601108", 784, 2676],
      [900, "80601109", 882, 3010],
      [1000, "80601110", 980, 3345],
      [1100, "80601111", 1078, 3679],
      [1200, "80601112", 1176, 4014],
      [1400, "80601114", 1372, 4683],
      [1600, "80601116", 1568, 5352],
      [1800, "80601118", 1764, 6021],
      [2000, "80601120", 1960, 6689],
    ],
  ],
  [
    600,
    "P+",
    [
      [400, "80602104", 538, 1836],
      [500, "80602105", 673, 2297],
      [600, "80602106", 807, 2754],
      [700, "80602107", 942, 3215],
      [800, "80602108", 1076, 3672],
      [900, "80602109", 1211, 4133],
      [1000, "80602110", 1345, 4590],
      [1100, "80602111", 1480, 5051],
      [1200, "80602112", 1614, 5509],
      [1400, "80602114", 1883, 6427],
      [1600, "80602116", 2152, 7345],
    ],
  ],
  [
    600,
    "K2",
    [
      [400, "80602204", 693, 2365],
      [500, "80602205", 866, 2956],
      [600, "80602206", 1039, 3546],
      [700, "80602207", 1212, 4137],
      [800, "80602208", 1386, 4730],
      [900, "80602209", 1559, 5321],
      [1000, "80602210", 1732, 5911],
      [1100, "80602211", 1905, 6502],
      [1200, "80602212", 2078, 7092],
      [1400, "80602214", 2425, 8277],
      [1600, "80602216", 2771, 9457],
      [1800, "80602218", 3118, 10642],
      [2000, "80602220", 3464, 11823],
    ],
  ],
  [
    700,
    "K1",
    [
      [400, "80701104", 447, 1526],
      [500, "80701105", 559, 1908],
      [600, "80701106", 670, 2287],
      [700, "80701107", 782, 2669],
      [800, "80701108", 894, 3051],
      [900, "80701109", 1005, 3430],
      [1000, "80701110", 1117, 3812],
      [1100, "80701111", 1229, 4195],
      [1200, "80701112", 1340, 4573],
      [1400, "80701114", 1564, 5338],
      [1600, "80701116", 1787, 6099],
      [1800, "80701118", 2011, 6864],
      [2000, "80701120", 2234, 7625],
    ],
  ],
  [
    700,
    "K2",
    [
      [400, "80702204", 784, 2676],
      [500, "80702205", 981, 3348],
      [600, "80702206", 1177, 4017],
      [700, "80702207", 1373, 4686],
      [800, "80702208", 1569, 5355],
      [900, "80702209", 1765, 6024],
      [1000, "80702210", 1961, 6693],
      [1100, "80702211", 2157, 7362],
      [1200, "80702212", 2353, 8031],
      [1400, "80702214", 2745, 9369],
      [1600, "80702216", 3138, 10710],
      [1800, "80702218", 3530, 12048],
      [2000, "80702220", 3922, 13386],
    ],
  ],
];

/**
 * The SECOND published table on the same sheet: "EN 442 CERTIFICATION DATA — CETIAT
 * TESTED IN ACCORDANCE WITH BS EN 442".
 *
 * Two columns of it are transcribed here, both keyed on panel type and height:
 *
 *   n — the characteristic exponent in output = rated × (ΔT_actual / ΔT_rated)^n.
 *       This is the number the derating in @sts/calc turns on, and Stelrad publish
 *       it per product as BS EN 442-2 requires. It is the reason no seeded entry
 *       falls back to the engine's provisional 1.3 default.
 *
 *   wattsPerMetre — output per metre of length at 75/65/20, i.e. the rate the
 *       output table above is built from. It is carried for one purpose: it
 *       cross-checks the whole output table from an independently printed figure.
 *       See emitter-catalogue.test.ts. It takes part in no calculation.
 *
 * The sheet prints these only for the type-and-height combinations it sells, which
 * is exactly the ten groups the output table covers.
 */
export const STELRAD_SOFTLINE_COMPACT_EN442: ReadonlyArray<
  readonly [
    panelType: PanelRadiatorType,
    heightMm: number,
    exponent: number,
    wattsPerMetre: number,
  ]
> = [
  ["K1", 300, 1.32, 509],
  ["K1", 450, 1.31, 756],
  ["K1", 600, 1.29, 980],
  ["K1", 700, 1.29, 1117],
  ["P+", 450, 1.34, 1055],
  ["P+", 600, 1.33, 1345],
  ["K2", 300, 1.33, 982],
  ["K2", 450, 1.33, 1371],
  ["K2", 600, 1.33, 1732],
  ["K2", 700, 1.34, 1961],
];

function publishedExponent(panelType: PanelRadiatorType, heightMm: number): number {
  const row = STELRAD_SOFTLINE_COMPACT_EN442.find(
    ([t, h]) => t === panelType && h === heightMm,
  );
  if (row === undefined) {
    // Not reachable from the seeded table, and deliberately a throw rather than a
    // fallback. A silent 1.3 here would be exactly the invented figure this whole
    // file exists to keep out, and it would arrive wearing the same type as a
    // published one.
    throw new Error(
      `No published BS EN 442 exponent for Stelrad Softline Compact ${panelType} at ` +
        `${heightMm} mm. Every size in the output table must have one; add the ` +
        `certification row before adding the size.`,
    );
  }
  return row[2];
}

function buildStelradEntries(): readonly RadiatorCatalogueEntry[] {
  const entries: RadiatorCatalogueEntry[] = [];
  for (const [heightMm, panelType, rows] of STELRAD_ROWS) {
    for (const [lengthMm, uin, watts, btu] of rows) {
      entries.push({
        id: `stelrad-softline-compact-${panelType.toLowerCase().replace("+", "plus")}-${heightMm}x${lengthMm}`,
        manufacturer: "Stelrad",
        range: "Softline Compact",
        model: `Softline Compact ${panelType} ${heightMm} × ${lengthMm}`,
        partNumber: uin,
        kind: "panel-radiator",
        panelType,
        heightMm,
        lengthMm,
        // depthMm deliberately absent: the source sheet does not print it.
        ratedOutputW: watts,
        ratedDeltaTK: 50,
        ratingBasis: "75/65/20",
        // Published, not assumed — see STELRAD_SOFTLINE_COMPACT_EN442 above.
        exponent: publishedExponent(panelType, heightMm),
        ratedOutputBtuPerHour: btu,
      });
    }
  }
  return entries;
}

export const STELRAD_SOFTLINE_COMPACT: Ruleset<RadiatorCatalogue> = {
  id: "catalogue.radiators.stelrad-softline-compact",
  version: "0.1.0-provisional",
  // The sheet carries no effective date of its own. This is the date the range is
  // known to have been on sale by, taken from the source document's own metadata,
  // not a date the standard sets.
  effectiveFrom: "2018-12-11",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "Stelrad Softline Compact specification sheet (K1 / P+ / K2 output tables)",
    issue: "undated sheet; PDF metadata dated 2018-12-11",
    clause:
    "ΔT50 (75/65/20°C) output table and the EN 442 certification data table " +
    "(CETIAT tested), heights 300/450/600/700 mm",
    issued: "2018-12-11",
    url: "https://assets.unilogcorp.com/267/ITEM/DOC/Stelrad_K2_12_40_Specification_Sheet.pdf",
  },
  data: {
    entries: buildStelradEntries(),
    note: {
      retrievedFrom:
        "https://assets.unilogcorp.com/267/ITEM/DOC/Stelrad_K2_12_40_Specification_Sheet.pdf " +
        "(a third-party product-data host, NOT stelrad.com — stelrad.com refused direct access)",
      retrievedOn: "2026-08-26",
      checks: [
        "All 102 rows — panel type, height, length, Stelrad UIN, watts and Btu/hr — " +
          "were re-extracted from the PDF named above and compared field by field " +
          "against this file on 2026-08-27. Every figure matched, nothing in the " +
          "sheet was omitted, and nothing is present here that is not in the sheet. " +
          "This is the check that matters: it compares the transcription against " +
          "the document rather than against itself.",
        "Re-verified independently on 2026-08-27 by a second person who had not " +
          "done the original transcription: the PDF was fetched again from the URL " +
          "below, its text extracted with `pdftotext -layout`, and the 102 " +
          "(UIN, watts, Btu/hr) triples compared as SETS against this file — 102 " +
          "found, 102 matched, none extra in either direction. The EN 442 " +
          "certification row was compared value by value in the same pass: all ten " +
          "n-coefficients and all ten W/m figures matched. So the transcription has " +
          "now been checked against the document twice, by two routes.",
        "Every watt figure reproduces the sheet's own printed Btu/hr column at " +
          "3.41214 Btu/hr per W to within 4 Btu/hr, i.e. within the rounding of the " +
          "underlying value. Two independent columns agreeing is what makes a " +
          "single-digit transcription slip detectable.",
        "Every 1000 mm output reproduces the W/m figure printed in the sheet's " +
          "separate EN 442 certification table. That table is set from different " +
          "type on a different page, so agreement is genuine corroboration and not " +
          "the output table checking itself.",
        "Within each height-and-type group, output is proportional to length to " +
          "within half a watt, which is what a table built from a published W/m " +
          "rate must look like. A misread digit breaks this.",
        "Every Stelrad UIN decodes to the height, panel type and length it is filed " +
          "under (80 · height/10 · type · length/100), so a part number cannot be " +
          "attached to the wrong size without the check failing.",
        "All of these run mechanically in emitter-catalogue.test.ts, so they keep " +
          "holding rather than having held once.",
      ],
      toVerify: [
        "Obtain the sheet from stelrad.com rather than a reseller's data host. This " +
          "is the whole of the gap between this catalogue and a verified one: the " +
          "numbers have been checked against the document, but the document has not " +
          "been checked against the manufacturer.",
        "Confirm the Softline Compact range and these UINs are still current, and " +
          "that nothing has been reissued at a different output since 2018. A rating " +
          "change would silently invalidate every selection made from this file, and " +
          "no internal check can detect it.",
        "Obtain overall depths. The sheet dimensions the side view with three depth " +
          "figures per type (K1 83/61/51, P+ 101/77/62, K2 124/100/73 mm) but only " +
          "labels the smallest, as wall-to-tap-centre, in the EN 442 table. Which of " +
          "the other two is the overall depth is carried by the drawing alone, so " +
          "none is recorded and no depth constraint can be applied.",
        "The BS EN 442-2 exponents ARE published on this sheet, per type and height, " +
          "and are transcribed here — so no seeded entry falls back to the engine's " +
          "provisional 1.3 default. They still inherit the same open question as the " +
          "outputs: they came from the reseller-hosted copy, not from Stelrad.",
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// Seed 2: Stelrad Compact, ΔT50 — a second, distinct Stelrad range
// ---------------------------------------------------------------------------

/**
 * Stelrad "Compact" — the range Stelrad's own omnibus specification book prints
 * simply as "Compact", and that Stelrad ALSO sells as a standalone datasheet titled
 * "Compact Horizontal" (to distinguish it from "Compact Vertex", the vertical
 * version). Both documents were fetched independently and carry byte-for-byte the
 * same UINs and watt/Btu figures for every shared size — genuine corroboration from
 * two separately-published Stelrad documents, not the same table read twice.
 *
 * ΔT50 (75/65/20°C), heights 300/450/600/700, lengths from 300/400 mm (depending on
 * height) up to 3000 mm — noticeably longer than Softline Compact's own 2000-2500 mm
 * ceiling, which is most of why this range adds so many rows.
 *
 * NOT the same product as STELRAD_SOFTLINE_COMPACT, despite sharing this engine's
 * "panel-radiator" kind and K1/P+/K2 panel types: Softline has a curved top grille
 * and end panels, Compact does not, and their UINs are on entirely separate number
 * ranges (80xxxxxx vs 143xxx). For the sizes both ranges happen to sell, the ΔT50
 * WATTS are identical (e.g. 450 mm P+ at 1000 mm = 1055 W in both) — the underlying
 * heat-emitting panel is evidently shared between the two styling variants. Their
 * published EXPONENTS at those same sizes are NOT identical (see
 * `STELRAD_COMPACT_EN442` below), which is recorded as an observed fact from two
 * separately-published Stelrad documents, not reconciled into one "true" value —
 * there is no basis in either document to say one of the two is the error.
 *
 * Reuses the same `PanelRow` tuple type Softline Compact's own rows are built from.
 */
const STELRAD_COMPACT_ROWS: ReadonlyArray<
  readonly [heightMm: number, type: PanelRadiatorType, rows: readonly PanelRow[]]
> = [
  [
    300,
    "K1",
    [
      [500, "143650", 255, 870],
      [1000, "143651", 509, 1737],
      [1500, "143652", 764, 2608],
      [2000, "143653", 1018, 3474],
      [2500, "143654", 1273, 4345],
      [3000, "143655", 1527, 5212],
    ],
  ],
  [
    300,
    "P+",
    [
      [500, "143656", 373, 1273],
      [1000, "143657", 745, 2543],
      [1500, "143658", 1118, 3816],
      [2000, "143659", 1490, 5085],
      [2500, "143660", 1863, 6358],
      [3000, "143661", 2235, 7628],
    ],
  ],
  [
    300,
    "K2",
    [
      [500, "143662", 491, 1676],
      [1000, "143663", 982, 3352],
      [1500, "143664", 1473, 5027],
      [2000, "143665", 1964, 6703],
      [2500, "143666", 2455, 8379],
      [3000, "143667", 2946, 10055],
    ],
  ],
  [
    450,
    "K1",
    [
      [400, "143680", 302, 1031],
      [500, "143681", 378, 1290],
      [600, "143682", 454, 1550],
      [700, "143683", 529, 1805],
      [800, "143684", 605, 2065],
      [900, "143685", 680, 2321],
      [1000, "143686", 756, 2580],
      [1100, "143687", 832, 2840],
      [1200, "143688", 907, 3096],
      [1400, "143689", 1058, 3611],
      [1600, "143690", 1210, 4130],
      [1800, "143691", 1361, 4645],
      [2000, "143692", 1512, 5160],
      [2200, "143693", 1663, 5676],
      [2400, "143694", 1814, 6191],
      [2600, "143695", 1966, 6710],
      [2800, "143696", 2117, 7225],
      [3000, "143697", 2268, 7741],
    ],
  ],
  [
    450,
    "P+",
    [
      [400, "143698", 422, 1440],
      [500, "143699", 528, 1802],
      [600, "143700", 633, 2160],
      [700, "143701", 739, 2522],
      [800, "143702", 844, 2881],
      [900, "143703", 950, 3242],
      [1000, "143704", 1055, 3601],
      [1100, "143705", 1161, 3962],
      [1200, "143706", 1266, 4321],
      [1400, "143707", 1477, 5041],
      [1600, "143708", 1688, 5761],
      [1800, "143709", 1899, 6481],
      [2000, "143710", 2110, 7201],
      [2200, "143711", 2321, 7922],
      [2400, "143712", 2532, 8642],
      [2600, "143713", 2743, 9362],
      [2800, "143714", 2954, 10082],
      [3000, "143715", 3165, 10802],
    ],
  ],
  [
    450,
    "K2",
    [
      [400, "143716", 548, 1870],
      [500, "143717", 686, 2341],
      [600, "143718", 823, 2809],
      [700, "143719", 960, 3276],
      [800, "143720", 1097, 3744],
      [900, "143721", 1234, 4212],
      [1000, "143722", 1371, 4679],
      [1100, "143723", 1508, 5147],
      [1200, "143724", 1645, 5614],
      [1400, "143725", 1919, 6550],
      [1600, "143726", 2194, 7488],
      [1800, "143727", 2468, 8423],
      [2000, "143728", 2742, 9358],
      [2200, "143729", 3016, 10294],
      [2400, "143730", 3290, 11229],
      [2600, "143731", 3565, 12167],
      [2800, "143732", 3839, 13103],
      [3000, "143733", 4113, 14038],
    ],
  ],
  [
    600,
    "K1",
    [
      [300, "143646", 294, 1003],
      [400, "143746", 392, 1338],
      [500, "143747", 490, 1672],
      [600, "143748", 588, 2007],
      [700, "143749", 686, 2341],
      [800, "143750", 784, 2676],
      [900, "143751", 882, 3010],
      [1000, "143752", 980, 3345],
      [1100, "143753", 1078, 3679],
      [1200, "143754", 1176, 4014],
      [1400, "143755", 1372, 4683],
      [1600, "143756", 1568, 5352],
      [1800, "143757", 1764, 6021],
      [2000, "143758", 1960, 6689],
      [2200, "143759", 2156, 7358],
      [2400, "143760", 2352, 8027],
      [2600, "143761", 2548, 8696],
      [2800, "143762", 2744, 9365],
      [3000, "143763", 2940, 10034],
    ],
  ],
  [
    600,
    "P+",
    [
      [400, "143764", 538, 1836],
      [500, "143765", 673, 2297],
      [600, "143766", 807, 2754],
      [700, "143767", 942, 3215],
      [800, "143768", 1076, 3672],
      [900, "143769", 1211, 4133],
      [1000, "143770", 1345, 4590],
      [1100, "143771", 1480, 5051],
      [1200, "143772", 1614, 5509],
      [1400, "143773", 1883, 6427],
      [1600, "143774", 2152, 7345],
      [1800, "143775", 2421, 8263],
      [2000, "143776", 2690, 9181],
      [2200, "143777", 2959, 10099],
      [2400, "143778", 3228, 11017],
      [2600, "143779", 3497, 11935],
      [2800, "143780", 3766, 12853],
      [3000, "143781", 4035, 13771],
    ],
  ],
  [
    600,
    "K2",
    [
      [300, "143647", 520, 1774],
      [400, "143782", 693, 2365],
      [500, "143783", 866, 2956],
      [600, "143784", 1039, 3546],
      [700, "143785", 1212, 4137],
      [800, "143786", 1386, 4730],
      [900, "143787", 1559, 5321],
      [1000, "143788", 1732, 5911],
      [1100, "143789", 1905, 6502],
      [1200, "143790", 2078, 7092],
      [1400, "143791", 2425, 8277],
      [1600, "143792", 2771, 9457],
      [1800, "143793", 3118, 10642],
      [2000, "143794", 3464, 11823],
      [2200, "143795", 3810, 13004],
      [2400, "143796", 4157, 14188],
      [2600, "143797", 4503, 15369],
      [2800, "143798", 4850, 16553],
      [3000, "143799", 5196, 17734],
    ],
  ],
  [
    700,
    "K1",
    [
      [300, "143648", 335, 1143],
      [400, "143812", 447, 1526],
      [500, "143813", 559, 1908],
      [600, "143814", 670, 2287],
      [700, "143815", 782, 2669],
      [800, "143816", 894, 3051],
      [900, "143817", 1005, 3430],
      [1000, "143818", 1117, 3812],
      [1100, "143819", 1229, 4195],
      [1200, "143820", 1340, 4573],
      [1400, "143821", 1564, 5338],
      [1600, "143822", 1787, 6099],
      [1800, "143823", 2011, 6864],
      [2000, "143824", 2234, 7625],
      [2200, "143825", 2457, 8386],
      [2400, "143826", 2681, 9150],
      [2600, "143827", 2904, 9911],
      [2800, "143828", 3128, 10676],
      [3000, "143829", 3351, 11437],
    ],
  ],
  [
    700,
    "P+",
    [
      [400, "143830", 612, 2089],
      [500, "143831", 765, 2611],
      [600, "143832", 918, 3133],
      [700, "143833", 1071, 3655],
      [800, "143834", 1224, 4178],
      [900, "143835", 1377, 4700],
      [1000, "143836", 1530, 5222],
      [1100, "143837", 1683, 5744],
      [1200, "143838", 1836, 6266],
      [1400, "143839", 2142, 7311],
      [1600, "143840", 2448, 8355],
      [1800, "143841", 2754, 9399],
      [2000, "143842", 3060, 10444],
      [2200, "143843", 3366, 11488],
      [2400, "143844", 3672, 12533],
      [2600, "143845", 3978, 13577],
      [2800, "143846", 4284, 14621],
      [3000, "143847", 4590, 15666],
    ],
  ],
  [
    700,
    "K2",
    [
      [300, "143649", 588, 2006],
      [400, "143848", 784, 2676],
      [500, "143849", 981, 3348],
      [600, "143850", 1177, 4017],
      [700, "143851", 1373, 4686],
      [800, "143852", 1569, 5355],
      [900, "143853", 1765, 6024],
      [1000, "143854", 1961, 6693],
      [1100, "143855", 2157, 7362],
      [1200, "143856", 2353, 8031],
      [1400, "143857", 2745, 9369],
      [1600, "143858", 3138, 10710],
      [1800, "143859", 3530, 12048],
      [2000, "143860", 3922, 13386],
      [2200, "143861", 4314, 14724],
      [2400, "143862", 4706, 16062],
      [2600, "143863", 5099, 17403],
      [2800, "143864", 5491, 18741],
      [3000, "143865", 5883, 20079],
    ],
  ],
];

/**
 * From the "Compact Horizontal" standalone datasheet's own EN 442 certification
 * table — the omnibus specification book that carries `STELRAD_COMPACT_ROWS` prints
 * the output table only and says "for EN442 data ... visit stelrad.com", so the
 * exponent and W/m figures here are transcribed from a SECOND, separately-fetched
 * Stelrad PDF, not from the same document as the outputs above.
 *
 * P1 (a single-panel, no-convector-fin type Stelrad also lists at 450/600 mm) is
 * printed in the same source table but is not part of the K1/P+/K2 range seeded
 * here, so it is left out rather than added as a fourth, unused panel type.
 */
export const STELRAD_COMPACT_EN442: ReadonlyArray<
  readonly [panelType: PanelRadiatorType, heightMm: number, exponent: number, wattsPerMetre: number]
> = [
  ["K1", 300, 1.32, 509],
  ["K1", 450, 1.31, 756],
  ["K1", 600, 1.29, 980],
  ["K1", 700, 1.29, 1117],
  ["P+", 300, 1.33, 745],
  ["P+", 450, 1.33, 1055],
  ["P+", 600, 1.34, 1345],
  ["P+", 700, 1.34, 1530],
  ["K2", 300, 1.33, 982],
  ["K2", 450, 1.33, 1371],
  ["K2", 600, 1.33, 1732],
  ["K2", 700, 1.34, 1961],
];

function publishedStelradCompactExponent(panelType: PanelRadiatorType, heightMm: number): number {
  const row = STELRAD_COMPACT_EN442.find(([t, h]) => t === panelType && h === heightMm);
  if (row === undefined) {
    throw new Error(
      `No published BS EN 442 exponent for Stelrad Compact ${panelType} at ${heightMm} mm. ` +
        "Every size in the output table must have one; add the certification row before adding the size.",
    );
  }
  return row[2];
}

function buildStelradCompactEntries(): readonly RadiatorCatalogueEntry[] {
  const entries: RadiatorCatalogueEntry[] = [];
  for (const [heightMm, panelType, rows] of STELRAD_COMPACT_ROWS) {
    for (const [lengthMm, uin, watts, btu] of rows) {
      entries.push({
        id: `stelrad-compact-${panelType.toLowerCase().replace("+", "plus")}-${heightMm}x${lengthMm}`,
        manufacturer: "Stelrad",
        range: "Compact",
        model: `Compact ${panelType} ${heightMm} × ${lengthMm}`,
        partNumber: uin,
        kind: "panel-radiator",
        panelType,
        heightMm,
        lengthMm,
        // depthMm deliberately absent: the "Compact Horizontal" datasheet dimensions
        // the same three-figure side view as Softline Compact's sheet, with the same
        // ambiguity over which figure is the overall depth. See the field doc above.
        ratedOutputW: watts,
        ratedDeltaTK: 50,
        ratingBasis: "75/65/20",
        exponent: publishedStelradCompactExponent(panelType, heightMm),
        ratedOutputBtuPerHour: btu,
      });
    }
  }
  return entries;
}

export const STELRAD_COMPACT: Ruleset<RadiatorCatalogue> = {
  id: "catalogue.radiators.stelrad-compact",
  version: "0.1.0-provisional",
  effectiveFrom: "2022-04-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard:
      "Stelrad Compact Horizontal specification sheet (K1 / P+ / K2 output and EN 442 tables)",
    issue: "Compact Horizontal datasheet; also printed in the April 2022 Stelrad specification book",
    clause: "ΔT50 (75/65/20°C) output table and EN 442 certification data, heights 300/450/600/700 mm",
    issued: "2022-04-01",
    url: "https://www.stelrad.com/wp-content/uploads/2015/04/Compact-H_Web.pdf",
  },
  data: {
    entries: buildStelradCompactEntries(),
    note: {
      retrievedFrom:
        "https://www.stelrad.com/wp-content/uploads/2015/04/Compact-H_Web.pdf and " +
        "https://www.stelrad.com/wp-content/uploads/2018/05/27271_Stelrad-Spec-Brochure_April-2022.pdf " +
        "— both fetched directly from stelrad.com, not a reseller.",
      retrievedOn: "2026-08-29",
      checks: [
        "All 184 rows were re-extracted from the omnibus specification book's own " +
          "table (pages 16-17) via both a text extraction and a rendered image of " +
          "the page, and the two were cross-read against each other where the text " +
          "extraction was ambiguous (several cells around 450 mm height carry an " +
          "overlapping depth-footnote glyph that the text layer garbles; the " +
          "rendered image resolves them cleanly). Done once, by one person, in one " +
          "session — see the file-level PROVENANCE note for what that gap means.",
        "The standalone 'Compact Horizontal' datasheet's own output table was also " +
          "extracted and compared UIN-for-UIN and watt-for-watt against the omnibus " +
          "book's table: all shared rows matched exactly. This is genuine " +
          "corroboration from a second, separately-published Stelrad document, not " +
          "the same table read twice.",
        "Every watt figure reproduces the sheet's own printed Btu/hr column at " +
          "3.41214 Btu/hr per W. Nine of the 184 rows — all at the largest lengths, " +
          "topping out at 5883 W — sit 4.0-5.4 Btu/hr outside the ±4 Btu/hr window " +
          "that fits Softline Compact's smaller range; the discrepancy scales with " +
          "output and is one-directional (Stelrad's printed Btu/hr is always very " +
          "slightly higher than watts×3.41214), which is the signature of accumulated " +
          "rounding at larger magnitudes, not a transcription slip — a transposed " +
          "digit fails by tens to hundreds of Btu/hr, not by five. " +
          "emitter-catalogue.test.ts checks this range with a tolerance that scales " +
          "with the value rather than Softline Compact's fixed ±4.",
        "Within each height-and-type group, output is proportional to length, " +
          "checked mechanically the same way as Softline Compact.",
        "K2 output exceeds P+ exceeds K1 at every shared height and length.",
        "The EN 442 exponent and W/m table was cross-checked against the omnibus " +
          "book at every 1000 mm length, where one exists: the W/m figure and the " +
          "1000 mm watts figure match exactly at all twelve (type, height) " +
          "combinations that have a 1000 mm size.",
      ],
      toVerify: [
        "Independent re-transcription by a second person, the way Softline Compact " +
          "was — this catalogue has only been checked once, by the one person who " +
          "built it.",
        "Confirm the range and these UINs are still current as of the design date, " +
          "and that nothing has been reissued at a different output since the April " +
          "2022 specification book (or the Compact Horizontal sheet's own undated " +
          "printing) was retrieved.",
        "Obtain overall depths — the same ambiguity Softline Compact's own toVerify " +
          "describes applies here, on the same manufacturer's drawing style.",
        "The nine large-length Btu/hr figures that sit outside a ±4 Btu/hr tolerance " +
          "are attributed here to rounding rather than transcription error, on the " +
          "strength of the pattern (small, one-directional, scaling with magnitude) " +
          "— but that attribution has not been confirmed against Stelrad directly.",
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// Seed 3: Purmo Compact, ΔT50 — a second manufacturer
// ---------------------------------------------------------------------------

/**
 * Purmo "Compact" (T11 / T21S / T22 / T33), from Purmo's own UK product catalogue —
 * purmo.com is Purmo Group's own site; this is a first-party source in the same
 * sense the Stelrad Compact datasheet above is, not a reseller copy.
 *
 * ΔT50 (75/65/20°C). Originally seeded at 450/600 mm only, with T11 (K1)/T21S (P+)/
 * T22 (K2) and a note that Purmo's catalogue also lists 300/750/900 mm heights and a
 * T33 (K3, triple panel) type "left for a follow-up". That follow-up is this
 * revision: every height and type Purmo currently sell in this range — T11 at
 * 300/450/600/750 mm, T21S at 450/600 mm only (Purmo genuinely do not sell 21S at
 * 300/750/900 — see the "-" cells in `PURMO_COMPACT_N`), T22 at 300/450/600/750 mm,
 * and T33 at 300/450/600/900 mm — is now transcribed from the SAME already-fetched
 * PDF. Nothing sellable in this range is left un-transcribed after this revision.
 *
 * The original 90 rows (450/600 mm, T11/T21S/T22) were re-verified against a
 * rendered image of the source page as part of adding the new rows, not just carried
 * forward unchecked — see the ruleset's own provenance note for what that comparison
 * found.
 *
 * Two things this range has that neither Stelrad range does:
 *
 * A published, unambiguous depth per type (`PURMO_COMPACT_DEPTH_MM` below) — a
 * single dimensioned side-view figure per type, not the three-figures-one-drawing
 * situation that leaves both Stelrad ranges' `depthMm` undefined. This is the first
 * catalogue in this file where a depth constraint in `selectEmitters` can actually
 * pass an entry rather than excluding everything.
 *
 * A published n-coefficient PER HEIGHT as well as per type (`PURMO_COMPACT_N` below)
 * — Stelrad publish one n per (type, height) pair too, but Purmo's own table has
 * genuine gaps (no T21S at 300/750/900 mm, no T11 at 900 mm, no T22 at 900 mm, no
 * T33 at 750 mm) that are left as gaps here rather than filled from a neighbouring
 * height — they are not missing transcription, they are sizes Purmo does not sell.
 */
const PURMO_COMPACT_ROWS: ReadonlyArray<
  readonly [heightMm: number, type: PanelRadiatorType, rows: readonly PanelRow[]]
> = [
  [
    300,
    "K1",
    [
      [600, "C11 300 0600", 328, 1119],
      [800, "C11 300 0800", 437, 1491],
      [1000, "C11 300 1000", 546, 1863],
      [1200, "C11 300 1200", 655, 2236],
      [1600, "C11 300 1600", 874, 2982],
      [2000, "C11 300 2000", 1092, 3726],
    ],
  ],
  [
    300,
    "K2",
    [
      [600, "C22 300 0600", 577, 1969],
      [800, "C22 300 0800", 769, 2624],
      [1000, "C22 300 1000", 961, 3279],
      [1200, "C22 300 1200", 1153, 3936],
      [1600, "C22 300 1600", 1538, 5248],
      [2000, "C22 300 2000", 1922, 6558],
    ],
  ],
  [
    450,
    "K1",
    [
      [400, "C11 450 0400", 316, 1078],
      [500, "C11 450 0500", 395, 1348],
      [600, "C11 450 0600", 474, 1617],
      [700, "C11 450 0700", 553, 1887],
      [800, "C11 450 0800", 632, 2156],
      [900, "C11 450 0900", 711, 2426],
      [1000, "C11 450 1000", 790, 2695],
      [1100, "C11 450 1100", 869, 2965],
      [1200, "C11 450 1200", 948, 3235],
      [1300, "C11 450 1300", 1027, 3504],
      [1400, "C11 450 1400", 1106, 3774],
      [1600, "C11 450 1600", 1264, 4313],
      [1800, "C11 450 1800", 1422, 4852],
      [2000, "C11 450 2000", 1580, 5391],
      [2300, "C11 450 2300", 1817, 6200],
    ],
  ],
  [
    450,
    "P+",
    [
      [400, "C21S 450 0400", 424, 1447],
      [500, "C21S 450 0500", 530, 1808],
      [600, "C21S 450 0600", 636, 2170],
      [700, "C21S 450 0700", 742, 2532],
      [800, "C21S 450 0800", 843, 2876],
      [900, "C21S 450 0900", 954, 3255],
      [1000, "C21S 450 1000", 1060, 3617],
      [1100, "C21S 450 1100", 1166, 3978],
      [1200, "C21S 450 1200", 1272, 4340],
      [1300, "C21S 450 1300", 1378, 4702],
      [1400, "C21S 450 1400", 1484, 5063],
      [1600, "C21S 450 1600", 1696, 5787],
      [1800, "C21S 450 1800", 1908, 6510],
      [2000, "C21S 450 2000", 2120, 7233],
      [2300, "C21S 450 2300", 2544, 8680],
    ],
  ],
  [
    450,
    "K2",
    [
      [400, "C22 450 0400", 539, 1839],
      [500, "C22 450 0500", 674, 2300],
      [600, "C22 450 0600", 808, 2757],
      [700, "C22 450 0700", 943, 3218],
      [800, "C22 450 0800", 1078, 3678],
      [900, "C22 450 0900", 1212, 4135],
      [1000, "C22 450 1000", 1347, 4596],
      [1100, "C22 450 1100", 1482, 5057],
      [1200, "C22 450 1200", 1616, 5514],
      [1300, "C22 450 1300", 1750, 5971],
      [1400, "C22 450 1400", 1886, 6435],
      [1600, "C22 450 1600", 2155, 7353],
      [1800, "C22 450 1800", 2425, 8274],
      [2000, "C22 450 2000", 2694, 9192],
      [2300, "C22 450 2300", 3097, 10567],
    ],
  ],
  [
    600,
    "K1",
    [
      [400, "C11 600 0400", 407, 1389],
      [500, "C11 600 0500", 509, 1737],
      [600, "C11 600 0600", 611, 2085],
      [700, "C11 600 0700", 713, 2433],
      [800, "C11 600 0800", 814, 2777],
      [900, "C11 600 0900", 916, 3125],
      [1000, "C11 600 1000", 1018, 3473],
      [1100, "C11 600 1100", 1120, 3821],
      [1200, "C11 600 1200", 1222, 4169],
      [1300, "C11 600 1300", 1304, 4449],
      [1400, "C11 600 1400", 1425, 4862],
      [1600, "C11 600 1600", 1629, 5558],
      [1800, "C11 600 1800", 1832, 6251],
      [2000, "C11 600 2000", 2036, 6947],
      [2300, "C11 600 2300", 2342, 7991],
    ],
  ],
  [
    600,
    "P+",
    [
      [400, "C21S 600 0400", 536, 1829],
      [500, "C21S 600 0500", 670, 2286],
      [600, "C21S 600 0600", 804, 2743],
      [700, "C21S 600 0700", 938, 3200],
      [800, "C21S 600 0800", 1072, 3658],
      [900, "C21S 600 0900", 1206, 4115],
      [1000, "C21S 600 1000", 1340, 4572],
      [1100, "C21S 600 1100", 1474, 5029],
      [1200, "C21S 600 1200", 1608, 5486],
      [1300, "C21S 600 1300", 1742, 5944],
      [1400, "C21S 600 1400", 1876, 6401],
      [1600, "C21S 600 1600", 2144, 7315],
      [1800, "C21S 600 1800", 2412, 8230],
      [2000, "C21S 600 2000", 2680, 9144],
      [2300, "C21S 600 2300", 3082, 10516],
    ],
  ],
  [
    600,
    "K2",
    [
      [400, "C22 600 0400", 684, 2334],
      [500, "C22 600 0500", 855, 2917],
      [600, "C22 600 0600", 1025, 3497],
      [700, "C22 600 0700", 1196, 4081],
      [800, "C22 600 0800", 1367, 4664],
      [900, "C22 600 0900", 1538, 5248],
      [1000, "C22 600 1000", 1709, 5831],
      [1100, "C22 600 1100", 1880, 6415],
      [1200, "C22 600 1200", 2051, 6998],
      [1300, "C22 600 1300", 2222, 7581],
      [1400, "C22 600 1400", 2393, 8166],
      [1600, "C22 600 1600", 2734, 9328],
      [1800, "C22 600 1800", 3076, 10495],
      [2000, "C22 600 2000", 3418, 11662],
      [2300, "C22 600 2300", 3931, 13413],
    ],
  ],
  [
    750,
    "K1",
    [
      [400, "C11 750 0400", 492, 1679],
      [500, "C11 750 0500", 615, 2098],
      [600, "C11 750 0600", 738, 2518],
      [700, "C11 750 0700", 861, 2938],
      [800, "C11 750 0800", 984, 3357],
      [900, "C11 750 0900", 1107, 3777],
      [1000, "C11 750 1000", 1230, 4197],
      [1100, "C11 750 1100", 1353, 4616],
      [1200, "C11 750 1200", 1476, 5036],
      [1300, "C11 750 1300", 1599, 5456],
      [1400, "C11 750 1400", 1722, 5875],
      [1600, "C11 750 1600", 1968, 6715],
      [1800, "C11 750 1800", 2214, 7554],
      [2000, "C11 750 2000", 2460, 8394],
    ],
  ],
  [
    750,
    "K2",
    [
      [400, "C22 750 0400", 822, 2805],
      [500, "C22 750 0500", 1028, 3506],
      [600, "C22 750 0600", 1233, 4207],
      [700, "C22 750 0700", 1439, 4908],
      [800, "C22 750 0800", 1644, 5609],
      [900, "C22 750 0900", 1850, 6310],
      [1000, "C22 750 1000", 2055, 7012],
      [1100, "C22 750 1100", 2261, 7713],
      [1200, "C22 750 1200", 2466, 8414],
      [1300, "C22 750 1300", 2672, 9115],
      [1400, "C22 750 1400", 2877, 9816],
      [1600, "C22 750 1600", 3288, 11219],
      [1800, "C22 750 1800", 3699, 12621],
      [2000, "C22 750 2000", 4110, 14023],
    ],
  ],
  // T33 (K3, triple panel convector) — a fourth panel type this catalogue did not
  // seed at all until this revision. Same source PDF, page 12-13 ("Compact (T33) -
  // Triple Panel Convector"). Purmo sell it at 300/450/600/900 mm only — no 750 mm,
  // confirmed by the "-" cell for K3 at 750 mm in PURMO_COMPACT_N below.
  [
    300,
    "K3",
    [
      [1000, "C33 300 1000", 1347, 4597],
      [1200, "C33 300 1200", 1616, 5517],
      [1400, "C33 300 1400", 1886, 6436],
      [1800, "C33 300 1800", 2425, 8275],
      [2000, "C33 300 2000", 2694, 9195],
    ],
  ],
  [
    450,
    "K3",
    [
      [800, "C33 450 0800", 1359, 4639],
      [1000, "C33 450 1000", 1699, 5799],
      [1200, "C33 450 1200", 2039, 6958],
      [1400, "C33 450 1400", 2379, 8118],
      [1800, "C33 450 1800", 3058, 10438],
      [2000, "C33 450 2000", 3398, 11597],
    ],
  ],
  [
    600,
    "K3",
    [
      [400, "C33 600 0400", 942, 3216],
      [600, "C33 600 0600", 1414, 4825],
      [800, "C33 600 0800", 1885, 6433],
      [1000, "C33 600 1000", 2356, 8041],
      [1200, "C33 600 1200", 2827, 9649],
      [1400, "C33 600 1400", 3298, 11257],
      [1800, "C33 600 1800", 4241, 14474],
      [2000, "C33 600 2000", 4712, 16082],
    ],
  ],
  [
    900,
    "K3",
    [
      [1000, "C33 900 1000", 3260, 11126],
      [1200, "C33 900 1200", 3912, 13352],
      [1400, "C33 900 1400", 4564, 15577],
    ],
  ],
];

/**
 * From Purmo's own "Compact - n-Coefficients" table. "-" cells in the source are
 * omitted — they are sizes Purmo does not sell, not gaps in this transcription (the
 * output tables above never seed a (type, height) combination this table is missing
 * an n for). The 300/750/900 mm rows and the whole K3 (T33) column were added in the
 * same revision that added the matching output rows above; 450/600 for K1/P+/K2 were
 * re-verified against the same rendered page image rather than only carried forward.
 */
export const PURMO_COMPACT_N: ReadonlyArray<
  readonly [panelType: PanelRadiatorType, heightMm: number, exponent: number]
> = [
  ["K1", 300, 1.2981],
  ["K1", 450, 1.3048],
  ["K1", 600, 1.3115],
  ["K1", 750, 1.3143],
  ["P+", 450, 1.3008],
  ["P+", 600, 1.3213],
  ["K2", 300, 1.3094],
  ["K2", 450, 1.3226],
  ["K2", 600, 1.3358],
  ["K2", 750, 1.3460],
  ["K3", 300, 1.314],
  ["K3", 450, 1.3313],
  ["K3", 600, 1.3486],
  ["K3", 900, 1.36],
];

/**
 * From Purmo's own dimensioned side-view drawing: one depth figure per TYPE,
 * independent of height, labelled unambiguously (unlike either Stelrad range's
 * three-figures-one-drawing situation). T11=type 11, T21S=type 21S, T22=type 22,
 * T33=type 33 — now seeded above, so this depth is no longer for an absent type.
 */
const PURMO_COMPACT_DEPTH_MM: Record<PanelRadiatorType, number> = {
  K1: 78,
  "P+": 97,
  K2: 129,
  K3: 170,
};

function publishedPurmoCompactExponent(
  panelType: PanelRadiatorType,
  heightMm: number,
): number | undefined {
  return PURMO_COMPACT_N.find(([t, h]) => t === panelType && h === heightMm)?.[2];
}

function buildPurmoCompactEntries(): readonly RadiatorCatalogueEntry[] {
  const entries: RadiatorCatalogueEntry[] = [];
  for (const [heightMm, panelType, rows] of PURMO_COMPACT_ROWS) {
    for (const [lengthMm, code, watts, btu] of rows) {
      entries.push({
        id: `purmo-compact-${panelType.toLowerCase().replace("+", "plus")}-${heightMm}x${lengthMm}`,
        manufacturer: "Purmo",
        range: "Compact",
        model: `Compact ${panelType} ${heightMm} × ${lengthMm}`,
        partNumber: code,
        kind: "panel-radiator",
        panelType,
        heightMm,
        lengthMm,
        depthMm: PURMO_COMPACT_DEPTH_MM[panelType],
        ratedOutputW: watts,
        ratedDeltaTK: 50,
        ratingBasis: "75/65/20",
        exponent: publishedPurmoCompactExponent(panelType, heightMm),
        ratedOutputBtuPerHour: btu,
      });
    }
  }
  return entries;
}

export const PURMO_COMPACT: Ruleset<RadiatorCatalogue> = {
  id: "catalogue.radiators.purmo-compact",
  version: "0.2.0-provisional",
  effectiveFrom: "2024-07-16",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "Purmo product range GB Catalogue July 2024 (Compact T11/T21S/T22/T33 output, " +
      "n-coefficient and dimension tables)",
    issue: "July 2024",
    clause: "ΔT50 (75/65/20°C) output tables, n-Coefficients table and side-view depths, " +
      "heights 300/450/600/750/900 mm (pages 10-13 of the PDF)",
    issued: "2024-07-16",
    url: "https://www.purmo.com/docs/Purmo-Catalogue-GB2024.pdf",
  },
  data: {
    entries: buildPurmoCompactEntries(),
    note: {
      retrievedFrom: "https://www.purmo.com/docs/Purmo-Catalogue-GB2024.pdf — fetched directly from purmo.com.",
      retrievedOn: "2026-08-29",
      checks: [
        "The original 90 rows (450/600 mm, T11/T21S/T22) were re-extracted from a " +
          "rendered image of the source page (pages 10-11 of the PDF), not from " +
          "machine-extracted text alone.",
        "This revision (0.2.0) adds 62 further rows from the SAME already-fetched " +
          "PDF, re-rendered at 300 dpi on 2026-08-30 for a cleaner read than the " +
          "original pass: T11 (K1) at 300 mm and 750 mm, T22 (K2) at 300 mm and " +
          "750 mm, and the entire T33 (K3) range at 300/450/600/900 mm (pages 12-13, " +
          "\"Compact (T33) - Triple Panel Convector\"). While re-rendering the page, " +
          "the ORIGINAL 90 rows (450/600 mm K1/P+/K2 output figures and their n- " +
          "coefficients) were also re-read against the cleaner image and matched " +
          "this file exactly, row for row — a second, independent look at the same " +
          "source rather than the original transcription being carried forward " +
          "unchecked.",
        "Every watt figure — old and new rows alike — reproduces the sheet's own " +
          "printed Btu/hr column at 3.41214 Btu/hr per W, within rounding.",
        "Output increases monotonically with length within every (type, height) " +
          "group — checked instead of strict proportionality, because this table is " +
          "not as clean on that measure as Stelrad's: the 1200-1300 mm step for " +
          "K1 600 mm (1222 W to 1304 W, +82) is visibly smaller than the steps " +
          "either side of it (+102 W per 100 mm elsewhere in the same group). That " +
          "was checked against a zoomed crop of the source image and is genuinely " +
          "what Purmo print, not a misread digit — real product lines are not " +
          "always perfectly linear at every length, and this file records what the " +
          "sheet says rather than smoothing it toward what a formula would predict.",
        "K2 output exceeds P+ output exceeds K1 output at every shared height and " +
          "length; K3 output exceeds K2 output at every shared height and length.",
        "The n-coefficient and depth tables were transcribed from the same PDF and " +
          "checked against the source image. Purmo's own n-coefficient table has " +
          "\"-\" cells for T21S at 300/750/900 mm, T11 at 900 mm, T22 at 900 mm, and " +
          "T33 at 750 mm — every one of those is ALSO a height Purmo do not print an " +
          "output row for at that type, so no (type, height) combination seeded in " +
          "the output tables above is left without a published n. Nothing was " +
          "invented to fill a gap, because after this revision there is no gap left " +
          "to fill: every currently-sold size in this range is now seeded.",
      ],
      toVerify: [
        "Independent re-transcription by a second person — the original 90 rows " +
          "have now been read twice by the one person who built this catalogue (see " +
          "the checks above), and the 62 new rows once; neither is the independent " +
          "SECOND PERSON check Softline Compact received.",
        "Confirm the July 2024 catalogue is still current as of the design date.",
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// Seed 4: Myson Select Compact, ΔT50 — a third manufacturer, structurally thinner
// ---------------------------------------------------------------------------

/**
 * Myson "SELECT Compact" (Type 11G / 21G / 22G), 600 mm height only, from Myson's
 * own "Panel Radiators Technical Guide" (dated 01.11.2013) as hosted by a merchant
 * (plumbnation.co.uk) rather than fetched from myson.co.uk directly — the same
 * category of gap Softline Compact's own provenance note describes.
 *
 * Two things this range does NOT have, unlike Purmo Compact and (for the exponent)
 * Stelrad Compact:
 *
 * No exponent. Myson's document does not publish a BS EN 442-2 characteristic n at
 * all — instead it gives a single, non-product-specific correction-factor lookup
 * table (e.g. ×0.87 at ΔT45, ×1.27 at ΔT60) applied uniformly to every Myson panel
 * radiator regardless of type or height. Converting that factor table into an
 * implied n would be a NUMBER THIS FILE COMPUTED, not one Myson published, so it is
 * not done — `exponent` is left undefined here, which is the honest reading of "the
 * manufacturer does not publish n for this product", not a gap in transcription.
 * `assessEmitters`/`selectEmitters` fall back to the provisional per-kind default
 * and warn accordingly, which is exactly the intended behaviour for that case.
 *
 * No depth. No dimensioned projection figure was found in this document.
 *
 * One row's Btu/hr is also missing on purpose, not by omission — see the comment
 * on the 2400 mm Type 11G row below.
 */
type MysonPanelRow = readonly [
  lengthMm: number,
  orderCode: string,
  watts: number,
  btu: number | undefined,
];

const MYSON_SELECT_COMPACT_ROWS: ReadonlyArray<
  readonly [heightMm: number, type: PanelRadiatorType, rows: readonly MysonPanelRow[]]
> = [
  [
    600,
    "K1",
    [
      [400, "SS 60 40G", 407, 1389],
      [500, "SS 60 50G", 509, 1737],
      [600, "SS 60 60G", 611, 2085],
      [700, "SS 60 70G", 713, 2433],
      [800, "SS 60 80G", 814, 2777],
      [900, "SS 60 90G", 916, 3125],
      [1000, "SS 60 100G", 1018, 3473],
      [1100, "SS 60 110G", 1120, 3821],
      [1200, "SS 60 120G", 1222, 4169],
      [1300, "SS 60 130G", 1323, 4514],
      [1400, "SS 60 140G", 1425, 4862],
      [1600, "SS 60 160G", 1629, 5558],
      [1800, "SS 60 180G", 1832, 6251],
      [2000, "SS 60 200G", 2036, 6947],
      // The source prints 2440 W / 7643 Btu/hr here — confirmed twice against a
      // zoomed crop of the source image, so 2440 W is genuinely what Myson print
      // for "SS 60 240G". But 2440 W × 3.41214 = 8326 Btu/hr, not 7643 — an 683
      // Btu/hr gap, an order of magnitude past every other row's rounding, and
      // there is a second, unrelated printing error elsewhere on the same source
      // page (a 700 mm Type 11G row prints "118" where the pattern implies "1187").
      // Rather than silently repeat what looks like Myson's own typesetting error,
      // or invent a "corrected" figure, the Btu/hr column for this one row is left
      // undefined: the watts figure is what a person ordering this UIN receives,
      // and it is the only one of the two this engine ever calculates from.
      [2400, "SS 60 240G", 2440, undefined],
    ],
  ],
  [
    600,
    "P+",
    [
      [400, "SX 60 40G", 542, 1849],
      [500, "SX 60 50G", 678, 2312],
      [600, "SX 60 60G", 813, 2774],
      [700, "SX 60 70G", 949, 3236],
      [800, "SX 60 80G", 1084, 3699],
      [900, "SX 60 90G", 1220, 4161],
      [1000, "SX 60 100G", 1355, 4623],
      [1100, "SX 60 110G", 1491, 5086],
      [1200, "SX 60 120G", 1626, 5548],
      [1300, "SX 60 130G", 1762, 6010],
      [1400, "SX 60 140G", 1897, 6473],
      [1600, "SX 60 160G", 2168, 7397],
      [1800, "SX 60 180G", 2439, 8322],
    ],
  ],
  [
    600,
    "K2",
    [
      [400, "SD 60 40G", 691, 2357],
      [500, "SD 60 50G", 864, 2946],
      [600, "SD 60 60G", 1036, 3536],
      [700, "SD 60 70G", 1209, 4125],
      [800, "SD 60 80G", 1382, 4714],
      [900, "SD 60 90G", 1554, 5303],
      [1000, "SD 60 100G", 1727, 5893],
      [1100, "SD 60 110G", 1900, 6482],
      [1200, "SD 60 120G", 2072, 7071],
      [1300, "SD 60 130G", 2245, 7660],
      [1400, "SD 60 140G", 2418, 8250],
      [1600, "SD 60 160G", 2763, 9428],
      [1800, "SD 60 180G", 3109, 10607],
      [2000, "SD 60 200G", 3454, 11785],
      [2400, "SD 60 240G", 4145, 14142],
    ],
  ],
];

function buildMysonSelectCompactEntries(): readonly RadiatorCatalogueEntry[] {
  const entries: RadiatorCatalogueEntry[] = [];
  for (const [heightMm, panelType, rows] of MYSON_SELECT_COMPACT_ROWS) {
    for (const [lengthMm, code, watts, btu] of rows) {
      entries.push({
        id: `myson-select-compact-${panelType.toLowerCase().replace("+", "plus")}-${heightMm}x${lengthMm}`,
        manufacturer: "Myson",
        range: "Select Compact",
        model: `Select Compact ${panelType} ${heightMm} × ${lengthMm}`,
        partNumber: code,
        kind: "panel-radiator",
        panelType,
        heightMm,
        lengthMm,
        // depthMm deliberately absent: not found in the source document.
        ratedOutputW: watts,
        ratedDeltaTK: 50,
        ratingBasis: "75/65/20",
        // exponent deliberately absent: Myson does not publish one for this range
        // — see the file comment above.
        ratedOutputBtuPerHour: btu,
      });
    }
  }
  return entries;
}

export const MYSON_SELECT_COMPACT: Ruleset<RadiatorCatalogue> = {
  id: "catalogue.radiators.myson-select-compact",
  version: "0.1.0-provisional",
  effectiveFrom: "2013-11-01",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "provisional",
  source: {
    standard: "Myson Panel Radiators Technical Guide, SELECT Compact heat outputs (Type 11G/21G/22G)",
    issue: "26531 Tech Guide - Panels, dated 01.11.2013",
    clause: "Heat outputs @ ΔT 50°C, order codes, Nominal Height 600 mm",
    issued: "2013-11-01",
    url: "https://www.plumbnation.co.uk/documents/myson-select-compact.pdf",
  },
  data: {
    entries: buildMysonSelectCompactEntries(),
    note: {
      retrievedFrom:
        "https://www.plumbnation.co.uk/documents/myson-select-compact.pdf " +
        "(a merchant-hosted copy, NOT myson.co.uk — the same category of gap as " +
        "STELRAD_SOFTLINE_COMPACT's own provenance note).",
      retrievedOn: "2026-08-29",
      checks: [
        "All 43 rows were re-extracted from a rendered image of the source page " +
          "(page 27 of the PDF), not from machine-extracted text alone.",
        "42 of the 43 watt figures reproduce the sheet's own printed Btu/hr column " +
          "at 3.41214 Btu/hr per W, within rounding. The one exception (2400 mm " +
          "Type 11G) is a discrepancy in Myson's OWN printed document, not a " +
          "transcription slip in this file — see the code comment on that row for " +
          "the check that established this, including a second, unrelated printing " +
          "error found elsewhere on the same source page.",
        "Output increases monotonically with length within every (type) group.",
        "K2 output exceeds P+ output exceeds K1 output at every shared length.",
      ],
      toVerify: [
        "Obtain the sheet from myson.co.uk rather than a merchant's document host.",
        "Independent re-transcription by a second person.",
        "Confirm the range and order codes are still current — this specific " +
          "document is from 2013, older than either Stelrad source used here.",
        "Obtain the BS EN 442-2 characteristic exponent, if Myson publish one " +
          "anywhere else — this document gives only a non-product-specific " +
          "correction-factor table, which this engine's `correctEmitterOutput` " +
          "does not consume.",
        "Obtain overall depths — not found in this document at all.",
        "Myson's own Select Compact range extends to heights 300/400/500/700 mm " +
          "and a Type 33G (not yet released as of this document's printing) not " +
          "transcribed here; 600 mm was chosen as the one height common to both " +
          "Stelrad ranges already seeded, to keep the initial cross-manufacturer " +
          "comparison meaningful.",
      ],
    },
  },
};

/**
 * Every radiator catalogue the engine knows about.
 *
 * Deliberately NOT folded into `ALL_RULESETS` and not covered by
 * `RULES_BUNDLE_VERSION`. That bundle version stamps "which edition of the MCS
 * scheme rules produced this calculation", and a manufacturer adding a radiator
 * size is not a change to the scheme. Conflating them would force a rules bundle
 * bump — and re-verification of every stored calculation — every time a catalogue
 * arrived.
 */
export const RADIATOR_CATALOGUES: readonly Ruleset<RadiatorCatalogue>[] = [
  STELRAD_SOFTLINE_COMPACT,
  STELRAD_COMPACT,
  PURMO_COMPACT,
  MYSON_SELECT_COMPACT,
];
